import axios from './apiConfig';
import {
  ExternoDTO,
  ExternoInsertDTO,
  ExternoUpdateDTO,
  ExternoWithProjetosDTO,
  ExternoWithTarefasDTO,
  PaginatedExternos,
} from '../types/externo';
import { Tarefa } from '../types/tarefa';
import { Projeto } from 'types/projeto';

// Get all externos with pagination
export const getExternosAPI = async (
  page: number = 0,
  size: number = 10
): Promise<PaginatedExternos> => {
  const response = await axios.get(`/externos/paged?page=${page}&size=${size}`);
  return response.data;
};

// Get all externos without pagination
export const getAllExternosAPI = async (): Promise<ExternoDTO[]> => {
  const response = await axios.get('/externos');
  return response.data;
};

// Get externo by ID
export const getExternoByIdAPI = async (id: number): Promise<ExternoDTO> => {
  try {
    const response = await axios.get(`/externos/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching externo:', error);
    throw error;
  }
};

// Get externo with projetos by ID
export const getExternoWithProjetosByIdAPI = async (
  id: number
): Promise<ExternoWithProjetosDTO> => {
  try {
    const response = await axios.get(`/externos/${id}/with-projetos`);
    return response.data;
  } catch (error) {
    console.error('Error fetching externo with projetos:', error);
    throw error;
  }
};

// Get externos by projeto ID
export const getExternosByProjetoIdAPI = async (
  projetoId: number
): Promise<ExternoDTO[]> => {
  try {
    console.log(`Fetching externos for projeto ID: ${projetoId}`);
    const response = await axios.get(`/projetos/${projetoId}/externos`);
    console.log('Response data:', response.data);
    return response.data;
  } catch (error) {
    console.error(
      `Error fetching externos for projeto ID ${projetoId}:`,
      error
    );
    if (axios.isAxiosError(error) && error.response) {
      console.error('Error status:', error.response.status);
      console.error('Error details:', error.response.data);
    }
    return [];
  }
};

/**
 * Adiciona um ou mais colaboradores externos a um projeto específico
 * @param projetoId O ID do projeto ao qual adicionar os externos
 * @param externoIds Array de IDs dos externos a serem adicionados
 * @returns O projeto atualizado com os externos associados
 */
export const addExternosToProjetoAPI = async (
  projetoId: number,
  externoIds: number[]
): Promise<Projeto> => {
  try {
    console.log(`Adding externos to projeto ID ${projetoId}:`, externoIds);
    const response = await axios.post(
      `/projetos/${projetoId}/externos`,
      externoIds
    );
    return response.data;
  } catch (error) {
    console.error(`Error adding externos to projeto ID ${projetoId}:`, error);
    if (axios.isAxiosError(error) && error.response) {
      console.error('Error status:', error.response.status);
      console.error('Error details:', error.response.data);
    }
    throw error;
  }
};

/**
 * Remove um colaborador externo de um projeto específico
 * @param projetoId O ID do projeto do qual remover o externo
 * @param externoId O ID do externo a ser removido
 * @returns O projeto atualizado sem o externo removido
 */
export const removeExternoFromProjetoAPI = async (
  projetoId: number,
  externoId: number
): Promise<Projeto> => {
  try {
    console.log(`Removing externo ${externoId} from projeto ID ${projetoId}`);
    const response = await axios.delete(
      `/projetos/${projetoId}/externos/${externoId}`
    );
    return response.data;
  } catch (error) {
    console.error(
      `Error removing externo ${externoId} from projeto ID ${projetoId}:`,
      error
    );
    if (axios.isAxiosError(error) && error.response) {
      console.error('Error status:', error.response.status);
      console.error('Error details:', error.response.data);
    }
    throw error;
  }
};

// Get externo with tarefas by ID
export const getExternoWithTarefasByIdAPI = async (
  id: number
): Promise<ExternoWithTarefasDTO> => {
  try {
    const response = await axios.get(`/externos/${id}/with-tarefas`);
    return response.data;
  } catch (error) {
    console.error('Error fetching externo with tarefas:', error);
    throw error;
  }
};

// Get tarefas by externo ID
export const getTarefasByExternoIdAPI = async (
  externoId: number
): Promise<Tarefa[]> => {
  try {
    const response = await axios.get(`/externos/${externoId}/tarefas`);
    return response.data;
  } catch (error) {
    console.error(
      `Error fetching tarefas for externo with id ${externoId}:`,
      error
    );
    throw error;
  }
};

// Create a new externo
export const createExternoAPI = async (
  externoData: ExternoInsertDTO
): Promise<ExternoDTO> => {
  try {
    const response = await axios.post('/externos', externoData);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      console.error('Error creating externo - Status:', error.response.status);
      console.error('Error details:', error.response.data);
    }
    throw error;
  }
};

// Update an existing externo
export const updateExternoAPI = async (
  id: number,
  externo: ExternoUpdateDTO
): Promise<ExternoDTO> => {
  try {
    const response = await axios.put(`/externos/${id}`, externo);
    return response.data;
  } catch (error) {
    console.error('Error updating externo:', error);
    throw error;
  }
};

// Delete an externo (soft delete)
export const deleteExternoAPI = async (id: number): Promise<void> => {
  try {
    await axios.delete(`/externos/${id}`);
  } catch (error) {
    console.error('Error deleting externo:', error);
    throw error;
  }
};

// Hard delete an externo
export const hardDeleteExternoAPI = async (id: number): Promise<void> => {
  try {
    await axios.delete(`/externos/${id}/hard`);
  } catch (error) {
    console.error('Error hard deleting externo:', error);
    throw error;
  }
};

// Search externos
export const searchExternosAPI = async (
  query: string
): Promise<ExternoDTO[]> => {
  try {
    const response = await axios.get('/externos/search', { params: { query } });
    return response.data;
  } catch (error) {
    console.error('Error searching externos:', error);
    throw error;
  }
};

// Get externos with filters
export const getExternosWithFiltersAPI = async (
  filters: {
    name?: string;
    email?: string;
    faseProjeto?: string;
    especialidade?: string;
  },
  page: number = 0,
  size: number = 10
): Promise<PaginatedExternos> => {
  try {
    let url = `/externos/filter?page=${page}&size=${size}`;

    // Add all filters to the URL
    if (filters.name) {
      url += `&name=${encodeURIComponent(filters.name)}`;
    }
    if (filters.email) {
      url += `&email=${encodeURIComponent(filters.email)}`;
    }
    if (filters.faseProjeto) {
      url += `&faseProjeto=${filters.faseProjeto}`;
    }
    if (filters.especialidade) {
      url += `&especialidade=${filters.especialidade}`;
    }

    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching externos with filters:', error);
    throw error;
  }
};
